import { useState, useCallback, useEffect, useRef } from 'react';
import { toast } from '@strivelabs/ui-core';
import { ProcessingStatus, useKeyValList } from '@/hooks/useKeyValOperations';
import { processBucketRows } from '@/utils/bucketProcessor';
import { getBucketConfig } from '@/constants/contentRefreshBucketRegistry';
import { POLLING_INTERVAL } from '@/constants';
import { log } from '@/lib/logger';
import type { KeywordRow, KeywordRowWithStatus } from '@/components/content-outline/types';

// Delay before starting polling to avoid immediate double-fetch on mount
const POLLING_START_DELAY = 1000;

export interface ContentOutlineDataConfig {
  workflowInstanceId: string;
  agentShortName: string;
  pollingInterval?: number;
  enabled?: boolean;
  /** Control fetch execution (defaults to true). Set to false to defer fetching until store phase completes. */
  shouldFetch?: boolean;
}

export interface ContentOutlineDataResult {
  data: KeywordRowWithStatus[];
  isLoading: boolean;
  isError: boolean;
}

export function useContentOutlineData({
  workflowInstanceId,
  agentShortName,
  pollingInterval = POLLING_INTERVAL,
  enabled = true,
  shouldFetch = true,
}: ContentOutlineDataConfig): ContentOutlineDataResult {
  const [data, setData] = useState<KeywordRowWithStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const { listBucket } = useKeyValList();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const bucketConfig = getBucketConfig(agentShortName);

  const fetchData = useCallback(async (isInitialLoad = false) => {
    if (!workflowInstanceId || !bucketConfig) return;
    if (isInitialLoad) setIsLoading(true);

    try {
      // Fetch only 4 buckets (no WAITING_FOR_APPROVAL bucket)
      const [todoRows, inProgressRows, completedRows, failedRows] = await Promise.all([
        listBucket(bucketConfig.todoBucket, workflowInstanceId, bucketConfig.appName),
        listBucket(bucketConfig.inProgressBucket, workflowInstanceId, bucketConfig.appName),
        listBucket(bucketConfig.completedBucket, workflowInstanceId, bucketConfig.appName),
        listBucket(bucketConfig.failedBucket, workflowInstanceId, bucketConfig.appName),
      ]);

      const allRows: KeywordRowWithStatus[] = [];
      const rowIdRef = { current: 0 };

      // Process rows from all buckets
      allRows.push(...processBucketRows<KeywordRow>(completedRows, ProcessingStatus.COMPLETED, rowIdRef));
      allRows.push(...processBucketRows<KeywordRow>(inProgressRows, ProcessingStatus.IN_PROGRESS, rowIdRef));
      allRows.push(...processBucketRows<KeywordRow>(todoRows, ProcessingStatus.PENDING, rowIdRef));
      allRows.push(...processBucketRows<KeywordRow>(failedRows, ProcessingStatus.FAILED, rowIdRef));

      // Sort: failed items first, then by updated_at (most recent first)
      allRows.sort((a, b) => {
        // Failed items always come first
        const statusComparison = (a._status === ProcessingStatus.FAILED ? -1 : 0) - (b._status === ProcessingStatus.FAILED ? -1 : 0);
        if (statusComparison !== 0) {
          return statusComparison;
        }

        // For items with the same failed/non-failed status, sort by updated_at
        const aDate = new Date(a.updated_at || a.created_at || 0).getTime();
        const bDate = new Date(b.updated_at || b.created_at || 0).getTime();
        return bDate - aDate; // Most recent first
      });

      log('info', 'Successfully fetched content outline data from all key-value store buckets', {
        totalRows: allRows.length,
      });

      setData(allRows);
      setIsError(false);
    } catch (error) {
      log('error', 'Failed to fetch content outline data', { error });
      setIsError(true);
      // Only show toast on initial load, not during polling
      if (isInitialLoad) {
        toast({
          title: "Error",
          description: "Failed to load content outline data. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [workflowInstanceId, bucketConfig, listBucket]);

  useEffect(() => {
    if (!enabled || !shouldFetch || !workflowInstanceId || !bucketConfig) {
      setIsLoading(false);
      return;
    }

    fetchData(true);

    const startTimer = setTimeout(() => {
      pollingIntervalRef.current = setInterval(() => fetchData(false), pollingInterval);
    }, POLLING_START_DELAY);

    return () => {
      clearTimeout(startTimer);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [enabled, shouldFetch, workflowInstanceId, bucketConfig, fetchData, pollingInterval]);

  return { data, isLoading, isError };
}
